import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useFormInstance } from '@/hooks/useFormInstance';
import { DynamicFormRenderer } from '@/components/forms/DynamicFormRenderer';
import { evaluateVisibility } from '@/components/forms/visibility-evaluator';
import { collectFieldKeys, mapErrorsToFields } from '@/components/forms/form-errors';
import { App } from '@/services/app-compat';
import type { AttachableRecordType, FormSchemaVersionDetailData } from '@/services/api-types';

/**
 * Renders one Custom Form (schema + instance) for a given host record as a tab
 * panel. Used by NPSDClientScreen (CLIENT hosts) and CaseReportFormScreen
 * (CASE_REPORT hosts). Decision row 7: exactly one active instance per
 * (schema, host).
 *
 * Edit/Save flow is owned by the parent screen:
 *   - `isEditMode` controls read-only vs. editable rendering.
 *   - `saveTrigger` is a counter the parent bumps when the host record saves;
 *     the panel saves its dirty values when the counter changes. A cancel that
 *     doesn't bump it reverts the panel instead.
 *   - `onValidationChange` reports required-field errors up so the parent can
 *     pre-validate before firing the host save.
 *   - `onDirtyChange` reports unsaved changes up so the parent can hold actions
 *     that must not race the form save (e.g. approval).
 */
export interface FormTabPanelProps {
  schemaId: number | null;
  /** Host record type the instance is attached to. */
  recordType: AttachableRecordType;
  /** Host record id. `null` before the host row has been saved. */
  recordId: number | null;
  /**
   * Lowercase singular noun for the host record, used in user-facing copy
   * (e.g. 'client', 'report'). The plural is formed by appending 's'.
   */
  hostNoun: string;
  isEditMode?: boolean;
  saveTrigger?: number;
  /** Called whenever this panel's required-field errors change. */
  onValidationChange?: (schemaId: number, errors: string[]) => void;
  /** Called whenever this panel gains or loses unsaved changes (edits or a pending clear). */
  onDirtyChange?: (schemaId: number, dirty: boolean) => void;
}

/** Find required, currently-visible fields with empty values. Returns labels. */
function findMissingRequired(
  schemaVersion: FormSchemaVersionDetailData,
  values: Record<string, unknown>,
): string[] {
  const visibility = evaluateVisibility(schemaVersion.sections, values);
  const missing: string[] = [];
  for (const section of schemaVersion.sections ?? []) {
    for (const field of section.fields ?? []) {
      if (!field.required) continue;
      if (visibility[field.key] === false) continue;
      const v = values[field.key];
      const isEmpty =
        v == null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0);
      if (isEmpty) missing.push(field.label || field.key);
    }
  }
  return missing;
}

export const FormTabPanel = memo(function FormTabPanel({
  schemaId,
  recordType,
  recordId,
  hostNoun,
  isEditMode = false,
  saveTrigger = 0,
  onValidationChange,
  onDirtyChange,
}: FormTabPanelProps) {
  const { theme } = useTheme();
  const hostNounPlural = hostNoun + 's';
  const {
    schema,
    instance,
    isLoading,
    error,
    serverErrors,
    clearServerErrors,
    save,
    softDelete,
    reload,
  } = useFormInstance({
    schemaId,
    recordType,
    recordId,
    autoLoad: true,
  });

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Pending-clear state. Click Clear + Confirm sets pendingClear=true and snapshots
  // the pre-clear values so the user can Undo before the host save fires. Save
  // commits (softDelete when an instance exists, otherwise just resets local state);
  // Cancel (top of the host screen) reverts the whole pending state.
  const [pendingClear, setPendingClear] = useState(false);
  const [preClearValues, setPreClearValues] = useState<Record<string, unknown> | null>(null);
  const [preClearIsDirty, setPreClearIsDirty] = useState(false);

  // Reset local values whenever instance or schema changes (initial load,
  // post-save, schema swap). Crucially, isEditMode is NOT in the deps —
  // resetting on isEditMode change would cause a brief UI flash to pre-edit
  // values during the save flight.
  useEffect(() => {
    setValues(instance?.values ?? {});
    setIsDirty(false);
    setPendingClear(false);
    setPreClearValues(null);
    setPreClearIsDirty(false);
    clearServerErrors();
  }, [instance, schemaId, clearServerErrors]);

  const handleChange = useCallback((next: Record<string, unknown>) => {
    setValues(next);
    setIsDirty(true);
    // Typing into the form re-engages the user — if they had pending-cleared,
    // this means they changed their mind. Undo the pending state silently so
    // the new values become the save target.
    setPendingClear(false);
    setPreClearValues(null);
    setPreClearIsDirty(false);
    // The user is correcting the issue — drop stale server errors so the red
    // outlines clear immediately. The next save will repopulate any errors
    // that still apply.
    clearServerErrors();
  }, [clearServerErrors]);

  // Map structured server errors → per-field messages keyed by field key.
  // Form-level errors (no `:fieldKey` suffix and no field slot) surface in a
  // panel at the top.
  const knownKeys = useMemo(
    () => collectFieldKeys(schema?.currentVersion ?? null),
    [schema],
  );
  const { fieldErrors, formErrors } = useMemo(
    () => mapErrorsToFields(serverErrors, knownKeys),
    [serverErrors, knownKeys],
  );

  // Visibility-aware required-field check. Memoized so the validation-report
  // effect only fires when the actual error set changes. While pendingClear is
  // true the save will softDelete, so required-field validation is irrelevant.
  // A form nobody has started (no instance, no edits) is not validated either —
  // otherwise adding a schema with a required field would block saving every
  // existing host record until someone fills that form in.
  const validationErrors = useMemo(() => {
    if (pendingClear) return [];
    if (!instance && !isDirty) return [];
    if (!schema?.currentVersion) return [];
    return findMissingRequired(schema.currentVersion, values);
  }, [schema, values, pendingClear, instance, isDirty]);

  // Report validation errors up to the parent so the host save can pre-validate.
  // Use a JSON-string compare to avoid notifying when array identity changes
  // but contents are equal.
  const lastReportedRef = useRef<string>('');
  useEffect(() => {
    if (schemaId == null || !onValidationChange) return;
    const serialized = JSON.stringify(validationErrors);
    if (serialized === lastReportedRef.current) return;
    lastReportedRef.current = serialized;
    onValidationChange(schemaId, validationErrors);
  }, [schemaId, validationErrors, onValidationChange]);

  const hasUnsavedChanges = isDirty || pendingClear;
  const lastReportedDirtyRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (schemaId == null || !onDirtyChange) return;
    if (hasUnsavedChanges === lastReportedDirtyRef.current) return;
    lastReportedDirtyRef.current = hasUnsavedChanges;
    onDirtyChange(schemaId, hasUnsavedChanges);
  }, [schemaId, hasUnsavedChanges, onDirtyChange]);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    // Pending-clear path: commit the clear here so the user's Clear button
    // becomes effective only when they confirm the host record save. If there
    // is no instance, the clear has no server-side work — just reset state.
    if (pendingClear) {
      if (!instance) {
        setPendingClear(false);
        setPreClearValues(null);
        setPreClearIsDirty(false);
        setIsDirty(false);
        return;
      }
      setIsSaving(true);
      try {
        const ok = await softDelete();
        setIsSaving(false);
        if (ok) {
          setPendingClear(false);
          setPreClearValues(null);
          setPreClearIsDirty(false);
          setValues({});
          setIsDirty(false);
          App.showPopupNotification?.(`${schema?.name ?? 'Form'} cleared from this ${hostNoun}.`);
        }
      } catch (e: unknown) {
        setIsSaving(false);
        const msg = e instanceof Error ? e.message : 'Clear failed.';
        App.showPopupNotification?.(`${schema?.name ?? 'Form'}: ${msg}`);
      }
      return;
    }

    if (!isDirty) return;
    if (validationErrors.length > 0) {
      // Parent should have caught this in pre-validation; defensive guard.
      App.showPopupNotification?.(
        `${schema?.name ?? 'Form'}: missing required field${validationErrors.length > 1 ? 's' : ''}: ${validationErrors.join(', ')}`,
      );
      return;
    }
    setIsSaving(true);
    try {
      const result = await save(values);
      setIsSaving(false);
      if (result) {
        setIsDirty(false);
        if (result.isStaleVersion) {
          App.showPopupNotification?.(
            `${schema?.name ?? 'Form'} saved. This form changed since you opened it, so it has been refreshed to the latest version.`,
          );
          reload();
        }
      }
    } catch (e: unknown) {
      setIsSaving(false);
      const msg = e instanceof Error ? e.message : 'Save failed.';
      App.showPopupNotification?.(`${schema?.name ?? 'Form'}: ${msg}`);
    }
  }, [isSaving, pendingClear, instance, softDelete, isDirty, validationErrors, save, values, reload, schema, hostNoun]);

  // Combined save + cancel reaction. Save path is detected by saveTrigger
  // changing; cancel path is detected by isEditMode going true→false WITHOUT
  // a saveTrigger bump. Either way the user-visible state is correct without
  // the flash bug that an isEditMode-driven reset would cause.
  const lastSaveTriggerRef = useRef(saveTrigger);
  const lastEditModeRef = useRef(isEditMode);
  useEffect(() => {
    const wasEditing = lastEditModeRef.current;
    const wasSaveTrigger = lastSaveTriggerRef.current;
    lastEditModeRef.current = isEditMode;

    if (saveTrigger !== wasSaveTrigger) {
      lastSaveTriggerRef.current = saveTrigger;
      if (isDirty || pendingClear) handleSave();
      return;
    }

    if (wasEditing && !isEditMode) {
      // Cancel: revert local values to the loaded instance and drop any
      // pending-clear state so the form returns to its loaded baseline.
      setValues(instance?.values ?? {});
      setIsDirty(false);
      setPendingClear(false);
      setPreClearValues(null);
      setPreClearIsDirty(false);
    }
  }, [isEditMode, saveTrigger, isDirty, pendingClear, handleSave, instance]);

  // Snapshot the current values for Undo and visually empty the form. The actual
  // softDelete (when an instance exists) is deferred to the host record save so
  // Clear honors the page's Edit/Save/Cancel contract just like every other edit.
  // No confirm modal: the action is fully reversible (Undo clear button + top-level
  // Cancel), so an "Are you sure?" step would be friction without value.
  const requestRemove = useCallback(() => {
    setPreClearValues(values);
    setPreClearIsDirty(isDirty);
    setValues({});
    setPendingClear(true);
    setIsDirty(true);
    clearServerErrors();
  }, [values, isDirty, clearServerErrors]);

  // Undo a pending clear: restore the pre-clear values and dirty state.
  const handleUndoClear = useCallback(() => {
    setValues(preClearValues ?? {});
    setIsDirty(preClearIsDirty);
    setPendingClear(false);
    setPreClearValues(null);
    setPreClearIsDirty(false);
  }, [preClearValues, preClearIsDirty]);

  if (!recordId) {
    return (
      <div className={'form-tab-panel' + (theme ? ' ' + theme : '')}>
        <div className="panel-body">Save the {hostNoun} first to begin entering form data.</div>
      </div>
    );
  }

  if (isLoading && !schema) {
    return (
      <div className={'form-tab-panel' + (theme ? ' ' + theme : '')}>
        <div className="panel-body">Loading form…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={'form-tab-panel' + (theme ? ' ' + theme : '')}>
        <div className="panel-body text-danger">Could not load form: {error}</div>
        <button className="btn btn-secondary" onClick={reload}>Retry</button>
      </div>
    );
  }

  if (!schema || !schema.currentVersion) {
    return (
      <div className={'form-tab-panel' + (theme ? ' ' + theme : '')}>
        <div className="panel-body">Form definition not available.</div>
      </div>
    );
  }

  const sections = schema.currentVersion.sections ?? [];
  const hasAnyField = sections.some((s) => (s.fields?.length ?? 0) > 0);

  return (
    <div className={'form-tab-panel' + (theme ? ' ' + theme : '')}>
      {!hasAnyField ? (
        <div className="alert alert-info" role="status">
          This form has no editable fields. An administrator can add fields in the Custom Forms screen.
        </div>
      ) : (
        <>
          {pendingClear && (
            <div className="alert alert-info" role="status" style={{ margin: '8px 0' }}>
              This form will be cleared for this {hostNoun} when you save. Click <strong>Undo clear</strong> below to keep the current data.
            </div>
          )}
          {formErrors.length > 0 && (
            <div className="kst-error-panel" role="alert" style={{ margin: '8px 0' }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {formErrors.map((e, i) => (
                  <li key={`form-err-${i}`} data-error-code={e.errorCode}>
                    {e.message ?? e.errorCode ?? 'Invalid form data'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DynamicFormRenderer
            schemaVersion={schema.currentVersion}
            values={values}
            onChange={handleChange}
            readOnly={!isEditMode}
            errors={fieldErrors}
          />
        </>
      )}
      {isEditMode && hasAnyField && (
        <div className="kstc-actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="kstc-btn kstc-btn--secondary"
            disabled={isSaving}
            onClick={pendingClear ? handleUndoClear : requestRemove}
            title={pendingClear
              ? 'Keep this form\'s current data instead of clearing it.'
              : `Clear this ${hostNoun}'s data for this form when you save. The form template and other ${hostNounPlural} are unaffected.`}
          >
            {pendingClear ? 'Undo clear' : 'Clear form'}
          </button>
        </div>
      )}
    </div>
  );
});
